import { useCallback, useEffect, useMemo } from 'react';

import { UTCDate } from '@date-fns/utc';
import {
  useDirectoryGetProductionCalendar,
  useCreateOrder as useCreateOrderApi,
  useOrderGetNewSchema,
  useValidateOrder as useValidateOrderApi,
} from 'api/requests';
import { OrderOperationType } from 'api/types/entity/account/order';
import { ErrorCode } from 'api/types/error';
import { convertDateByTimezone, lodash, toZeroTime } from 'helpers';
import { useObjectState, useServerErrorNotify } from 'hooks';
import { t } from 'i18next';
import { ROUTES, useNavigate } from 'libs/navigation';
import { useNotify } from 'libs/notify';
import { useOwner } from 'modules/account/common/hooks/useOwner';

import { OrderFormType, FormErrors } from '../../common/forms/orderForm';
import { parseOrderValuesForCreate } from '../helpers/parseOrderValuesForCreate';
import { parseOrderValuesForValidate } from '../helpers/parseOrderValuesForValidate';

import { useOrderInitialValues } from './useOrderInitialValues';

export const useCreateOrder = ({
  orderType,
}: {
  orderType: OrderOperationType;
}) => {
  const { navigate } = useNavigate();
  const notify = useNotify();

  const { orderSchema, loading: loadingNewSchema } = useOrderGetNewSchema();

  const { productionCalendar, loading: loadingProductionCalendar } =
    useDirectoryGetProductionCalendar();

  const {
    validateOrder,
    loading: validationLoading,
    error: validateError,
  } = useValidateOrderApi();
  const {
    createOrder,
    loading: submitLoading,
    error: submitError,
  } = useCreateOrderApi();

  const { ownerId, ownerType } = useOwner();

  const [formError, setFormError] = useObjectState<FormErrors>({});

  const initialValues = useOrderInitialValues(orderType, orderSchema);

  const minEndDate = useMemo(() => {
    if (!orderSchema?.minEndDate) return undefined;
    return toZeroTime(
      convertDateByTimezone(new UTCDate(orderSchema.minEndDate), 18000)
    );
  }, [orderSchema?.minEndDate]);

  const disabledDates = lodash
    .compact(productionCalendar?.nonBusinessDays)
    .map((date) => convertDateByTimezone(new UTCDate(date), 18000));

  // GMT+5 (Алматы)
  // TODO: Feature request to get broker timezone offset from API
  const brokerTimezoneOffset = 18000;

  const handleValidateOrder = useCallback(
    async (values: OrderFormType) => {
      const params = parseOrderValuesForValidate(orderType, values);
      return validateOrder(ownerId, ownerType, params);
    },
    [orderType, ownerId, ownerType, validateOrder]
  );

  const onSubmit = async (values: OrderFormType) => {
    if (values.otpCode) {
      const params = parseOrderValuesForCreate(orderType, values);
      const order = await createOrder(
        ownerId,
        ownerType,
        values.otpCode,
        params
      );
      if (order) {
        notify.info(t('account.createOrder.successMessage'));
        navigate(ROUTES.account.orders.fullPath, {});
      }
    }
  };

  useEffect(() => {
    if (submitError) {
      if (submitError.code === ErrorCode.INVALID_OTP_TOKEN) {
        setFormError({ otpCode: submitError.message });
      } else {
        setFormError({ otpCode: undefined });
        notify.error(submitError);
      }
    } else {
      setFormError({ otpCode: undefined });
    }
  }, [notify, setFormError, submitError]);

  useServerErrorNotify(validateError);

  return {
    submitLoading: validationLoading || submitLoading,
    initialValues,
    loading: loadingNewSchema || loadingProductionCalendar,
    formError,
    disabledDates,
    orderSchema,
    minEndDate,
    brokerTimezoneOffset,
    onSubmit,
    validateOrder: handleValidateOrder,
  };
};
